import { useEffect, useState } from "react";
import { App, Modal, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/api/client";
import { brandModalBtnProps } from "@/components/BrandButton";
import { ConfirmChangesModal } from "./ConfirmChangesModal";
import { LineNumberedTextarea } from "./LineNumberedTextarea";
import {
  SECURITY_MAX,
  deletedItems,
  invalidLineIndexes,
  parseBatchLines,
  type SecurityKind,
} from "./rules";

const FIELD_KEY = {
  redirect: "security.batchFieldRedirect",
  ip: "security.batchFieldIp",
  h5: "security.batchFieldH5",
} as const;

export function BatchModifyModal({
  open,
  kind,
  list,
  onCancel,
  onSave,
}: {
  open: boolean;
  kind: SecurityKind;
  list: string[];
  onCancel: () => void;
  onSave: (items: string[]) => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [text, setText] = useState("");
  const [errorLines, setErrorLines] = useState<number[]>([]);
  const [errorText, setErrorText] = useState("");
  const [pending, setPending] = useState<string[] | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setText(list.join("\n"));
    setErrorLines([]);
    setErrorText("");
    setPending(null);
  }, [open, list]);

  const trySave = () => {
    const invalid = invalidLineIndexes(kind, text);
    if (invalid.length > 0) {
      setErrorLines(invalid);
      setErrorText(
        kind === "ip" ? t("security.invalidIp") : t("security.batchInvalidUrl"),
      );
      return;
    }

    const next = parseBatchLines(text);
    if (next.length > SECURITY_MAX) {
      setErrorText(t("security.maxReached", { max: SECURITY_MAX }));
      return;
    }

    const removed = deletedItems(list, next);
    if (removed.length > 0) {
      setPending(next);
      return;
    }

    void commit(next);
  };

  const confirmSave = () => {
    if (!pending) return;
    void commit(pending);
  };

  const commit = (items: string[]) => {
    if (saving) return;
    setSaving(true);
    void Promise.resolve(onSave(items))
      .then(() => {
        setPending(null);
        message.success(t("security.batchSaved"));
      })
      .catch((err: unknown) => {
        const text =
          err instanceof ApiError && err.message
            ? err.message
            : t("security.batchFailed");
        message.error(text);
      })
      .finally(() => setSaving(false));
  };

  return (
    <>
      <Modal
        open={open}
        centered
        width={520}
        title={t("security.batchModify")}
        okText={t("common.save")}
        cancelText={t("common.cancel")}
        {...brandModalBtnProps()}
        confirmLoading={saving}
        onOk={trySave}
        onCancel={onCancel}
        destroyOnHidden
      >
        <Typography.Text>
          {t(FIELD_KEY[kind])}
          <Typography.Text type="danger"> *</Typography.Text>
        </Typography.Text>
        <Typography.Paragraph type="secondary" className="!mt-1 !mb-3">
          {t("security.batchHint", { max: SECURITY_MAX })}
        </Typography.Paragraph>
        <LineNumberedTextarea
          value={text}
          errorLines={errorLines}
          onChange={(next) => {
            setText(next);
            setErrorLines([]);
            setErrorText("");
          }}
        />
        {errorText ? (
          <Typography.Text type="danger" className="!mt-2 block">
            {errorText}
          </Typography.Text>
        ) : null}
      </Modal>

      <ConfirmChangesModal
        open={pending !== null}
        deleted={pending ? deletedItems(list, pending) : []}
        onCancel={() => {
          if (!saving) setPending(null);
        }}
        onConfirm={confirmSave}
        confirmLoading={saving}
      />
    </>
  );
}
